import {
  Box,
  Button,
  CheckboxInput,
  DateInput,
  Modal,
  TextInput,
  SelectInput,
  toast,
} from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';

import {
  AllocationTermOptions,
  AssetIncentiveAllocation,
  Clientize,
  IncentiveAllocationStatus,
  RECMonetizationMethodLabels,
} from 'types';

import { useUpdateAllocationMutation } from '../../services/incentives';

interface EditAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  allocation: Clientize<AssetIncentiveAllocation>;
  incentiveKey: string;
}

const monetizationMethodOptions = Object.entries(RECMonetizationMethodLabels).map(([value, label]) => ({
  value,
  label,
}));

const termOptions = AllocationTermOptions.map((opt) => ({
  value: opt.value,
  label: opt.label,
}));

const validationSchema = yup.object({
  name: yup.string().required('Allocation name is required'),
  allocationDate: yup.date().required('Allocation date is required'),
  term: yup.string().required('Term is required'),
});

export const EditAllocationModal = ({ isOpen, onClose, allocation, incentiveKey }: EditAllocationModalProps) => {
  const [updateAllocation] = useUpdateAllocationMutation();
  const [markAsComplete, setMarkAsComplete] = useState(false);

  const isCompleted = allocation.status === IncentiveAllocationStatus.completed;

  const formik = useFormik({
    initialValues: {
      name: allocation.name || '',
      allocationDate: allocation.allocationDate ? new Date(allocation.allocationDate) : null,
      term: allocation.term || '',
      monetizationMethod: allocation.monetizationMethod || '',
      recOwner: allocation.recOwner || 'Palmetto',
      partnerOrBuyer: allocation.partnerOrBuyer || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values: FormikValues, { setSubmitting }) => {
      try {
        await updateAllocation({
          key: incentiveKey,
          allocationId: allocation.id,
          data: {
            name: values.name,
            allocationDate: values.allocationDate?.toISOString(),
            term: values.term,
            monetizationMethod: values.monetizationMethod || undefined,
            recOwner: values.recOwner || undefined,
            partnerOrBuyer: values.partnerOrBuyer || undefined,
            ...(markAsComplete && { status: IncentiveAllocationStatus.completed }),
          },
        }).unwrap();

        if (markAsComplete) {
          toast.success('Allocation marked as complete');
        } else {
          toast.success('Allocation updated successfully');
        }

        onClose();
      } catch (error: any) {
        console.error('Error updating allocation:', error);
        toast.error(error?.data?.message || 'Error updating allocation');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setMarkAsComplete(false);
    onClose();
  };

  return (
    <Modal ariaLabelledBy="editAllocationModal" isOpen={isOpen} onDismiss={handleClose} maxWidth="600px">
      <Modal.Header id="editAllocationModal" title={`Edit Allocation: ${allocation.name}`} onDismiss={handleClose} />
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body background="secondary" overflow="auto" maxHeight="70vh">
          {isCompleted ? (
            <Box padding="lg" background="info" radius="md" margin="0 0 md 0" color="body-primary">
              This allocation is marked as complete and cannot be edited.
            </Box>
          ) : null}
          <Box direction="column" childGap="md">
            <TextInput
              type="text"
              label="Allocation Name"
              name="name"
              id="name"
              value={formik.values.name}
              onChange={(event) => formik.setFieldValue('name', event.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.name && (formik.errors.name as string)}
              isRequired
              isDisabled={isCompleted}
            />

            <DateInput
              id="allocationDate"
              name="allocationDate"
              label="Allocation Date"
              placeholder="MM-DD-YYYY"
              value={formik.values.allocationDate ? formik.values.allocationDate.toLocaleDateString('en-US') : ''}
              error={formik.touched.allocationDate && (formik.errors.allocationDate as string)}
              isRequired
              isDisabled={isCompleted}
              datePickerProps={{
                onChange: (date) => formik.setFieldValue('allocationDate', date),
                selected: formik.values.allocationDate || undefined,
                disabled: isCompleted,
              }}
              textInputProps={{}}
            />

            <SelectInput
              label="Term"
              name="term"
              id="term"
              value={termOptions.find((opt) => opt.value === formik.values.term) || null}
              onChange={(event) => formik.setFieldValue('term', event.target.value?.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.term && (formik.errors.term as string)}
              options={termOptions}
              isRequired
              isDisabled={isCompleted}
            />

            <SelectInput
              label="Monetization Method"
              name="monetizationMethod"
              id="monetizationMethod"
              value={monetizationMethodOptions.find((opt) => opt.value === formik.values.monetizationMethod) || null}
              onChange={(event) => formik.setFieldValue('monetizationMethod', event.target.value?.value || '')}
              onBlur={formik.handleBlur}
              options={monetizationMethodOptions}
              isClearable
              isDisabled={isCompleted}
            />

            <TextInput
              type="text"
              label="REC Owner"
              name="recOwner"
              id="recOwner"
              value={formik.values.recOwner}
              onChange={(event) => formik.setFieldValue('recOwner', event.target.value)}
              onBlur={formik.handleBlur}
              helpText="Defaults to 'Palmetto'"
              isDisabled={isCompleted}
            />

            <TextInput
              type="text"
              label="Partner or Buyer"
              name="partnerOrBuyer"
              id="partnerOrBuyer"
              value={formik.values.partnerOrBuyer}
              onChange={(event) => formik.setFieldValue('partnerOrBuyer', event.target.value)}
              onBlur={formik.handleBlur}
              isDisabled={isCompleted}
            />

            {!isCompleted && (
              <Box padding="md" background="warning" radius="md" margin="md 0 0 0">
                <CheckboxInput
                  id="markAsComplete"
                  name="markAsComplete"
                  label="Mark allocation as complete"
                  isChecked={markAsComplete}
                  onChange={(event) => setMarkAsComplete(event.target.checked)}
                  helpText="This will lock the allocation and mark all accounts as complete. Accounts in other allocations will be blocked."
                />
              </Box>
            )}
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            as="button"
            onClick={handleClose}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={formik.isSubmitting}
          >
            {isCompleted ? 'Close' : 'Cancel'}
          </Button>
          {!isCompleted && (
            <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting}>
              {markAsComplete ? 'Save & Complete' : 'Save Changes'}
            </Button>
          )}
        </Modal.Footer>
      </form>
    </Modal>
  );
};
